import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

function FakeLogin({ onAuth }) {
  const [stage, setStage] = useState("typing-username");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (stage === "typing-username") {
      const targetUsername = "root";
      let index = 0;
      const usernameInterval = setInterval(() => {
        if (index < targetUsername.length) {
          setUsername(targetUsername.slice(0, index + 1));
          index++;
        } else {
          clearInterval(usernameInterval);
          setTimeout(() => setStage("typing-password"), 500);
        }
      }, 100);
      return () => clearInterval(usernameInterval);
    }

    if (stage === "typing-password") {
      let index = 0;
      const passwordInterval = setInterval(() => {
        if (index < 10) {
          setPassword("*".repeat(index + 1));
          index++;
        } else {
          clearInterval(passwordInterval);
          setTimeout(() => setStage("processing"), 500);
        }
      }, 80);
      return () => clearInterval(passwordInterval);
    }

    if (stage === "processing") {
      setTimeout(onAuth, 1200);
    }
  }, [stage, onAuth]);

  return (
    <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950">
      <div className="w-full max-w-2xl p-8 bg-slate-900/50 backdrop-blur border border-cyan-500/20 rounded-lg shadow-2xl">
        <div className="mb-6 text-cyan-400 font-mono text-sm">
          <p>dashboard.andrwsyverson secure shell v2.1.4</p>
          <p className="text-cyan-600">Establishing encrypted connection...</p>
        </div>
        <div className="font-mono text-base space-y-3">
          {(stage === "typing-username" || stage === "typing-password" || stage === "processing") && (
            <div className="text-cyan-300">
              <span className="text-cyan-500">user@dashboard.andrewsyverson.me:~$</span>
              <span className="ml-2">login: {username}</span>
              {stage === "typing-username" && <span className="text-cyan-400 animate-pulse">▊</span>}
            </div>
          )}
          
          {(stage === "typing-password" || stage === "processing") && (
            <div className="flex items-center text-cyan-300">
              <span className="text-cyan-500">password:</span>
              <span className="ml-2">{password}</span>
              {stage === "typing-password" && <span className="text-cyan-400 animate-pulse">▊</span>}
            </div>
          )}
          
          {stage === "processing" && (
            <div className="space-y-2 mt-4">
              <p className="text-green-400">✓ Authentication successful</p>
              <p className="text-cyan-400">→ Establishing secure connection...</p>
              <p className="text-cyan-400">→ Loading environment modules...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const bootLines = [
  { text: "[  OK  ] Starting systemd-journald.service", color: "text-green-400" },
  { text: "[  OK  ] Mounted /sys/kernel/config", color: "text-green-400" },
  { text: "[  OK  ] Reached target Local File Systems", color: "text-green-400" },
  { text: "[ INFO ] Initializing secure socket connection to dashboard.andrewsyverson.me", color: "text-blue-400" },
  { text: "[ INFO ] Performing DNS lookup → 192.168.1.102", color: "text-blue-400" },
  { text: "[ INFO ] ssh-keyscan: connecting on port 22", color: "text-blue-400" },
  { text: "[ AUTH ] Authenticating as user: andrew.syverson", color: "text-yellow-400" },
  { text: "[ AUTH ] Handshake complete ✓", color: "text-green-400" },
  { text: "[ LOAD ] Pulling host telemetry stream...", color: "text-cyan-400" },
  { text: "[ LOAD ] Initializing SIEM dashboards...", color: "text-cyan-400" },
  { text: "[ LOAD ] Importing Wazuh agent telemetry...", color: "text-cyan-400" },
  { text: "[ LOAD ] Connecting to Proxmox hypervisor node...", color: "text-cyan-400" },
  { text: "[ DONE ] Environment ready — connection stable", color: "text-green-400" },
  { text: "[ LOG  ] Welcome back, Andrew", color: "text-purple-400" },
  { text: "[ EXEC ] sudo systemctl start portfolio.service", color: "text-yellow-300" },
  { text: "[  OK  ] Service running on port 443 (HTTPS)", color: "text-green-400" },
  { text: "[ SYS  ] Launching UI...", color: "text-cyan-300" },
];

function TerminalIntro({ onFinish }) {
  const [displayed, setDisplayed] = useState([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      if (i < bootLines.length) {
        setDisplayed((prev) => [...prev, bootLines[i]]);
        i++;
      } else {
        clearInterval(interval);
        setTimeout(() => setDone(true), 1000);
      }
    }, 200);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (done && onFinish) {
      setTimeout(() => onFinish(), 800);
    }
  }, [done, onFinish]);

  return (
    <div className="relative h-screen w-full overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        {Array.from({ length: 30 }).map((_, i) => (
          <div
            key={i}
            className="absolute text-cyan-500 text-xs font-mono whitespace-pre"
            style={{
              left: `${Math.random() * 100}%`,
              animation: `matrixFall ${10 + Math.random() * 5}s linear infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          >
            {Array.from({ length: 25 }).map(() => Math.random() > 0.5 ? "1" : "0").join("\n")}
          </div>
        ))}
      </div>

      <div className="relative z-10 flex flex-col justify-center h-full p-8 max-w-4xl mx-auto">
        <div className="bg-slate-900/60 backdrop-blur-sm border border-cyan-500/20 rounded-lg p-6 shadow-2xl">
          {displayed.map((line, i) => {
            if (!line) return null;
            return (
              <motion.p
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.15 }}
                className={`font-mono text-sm ${line.color || 'text-cyan-400'} mb-1`}
              >
                {line.text}
              </motion.p>
            );
          })}
          {!done && (
            <motion.span
              className="text-cyan-400 font-mono"
              animate={{ opacity: [1, 0] }}
              transition={{ repeat: Infinity, duration: 0.8 }}
            >
              ▊
            </motion.span>
          )}
        </div>
      </div>

      <style>{`
        @keyframes matrixFall {
          0% {
            transform: translateY(-100%);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          100% {
            transform: translateY(100vh);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

function useTypingEffect(text, speed = 100) {
  const [displayedText, setDisplayedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    let index = 0;
    setDisplayedText("");
    setIsComplete(false);

    const interval = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;
      } else {
        setIsComplete(true);
        clearInterval(interval);
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed]);

  return { displayedText, isComplete };
}

function HomeLabDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch('https://api.andrewsyverson.me/api/proxmox/dashboard')
      .then(res => res.json())
      .then(data => {
        setData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching dashboard data:', err);
        setError(true);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-700 rounded w-3/4"></div>
          <div className="h-4 bg-slate-700 rounded w-1/2"></div>
          <div className="h-4 bg-slate-700 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (error || !data || !data.success) {
    return (
      <div className="bg-slate-900/50 border border-red-500/20 rounded-lg p-6">
        <p className="text-red-400 font-mono text-sm">Error loading dashboard data. Please try again later.</p>
      </div>
    );
  }

  const runningVMs = data.vms.filter(vm => vm.status === "running").length;
  const totalVMs = data.vms.length;
  const node1 = data.cluster_health[0];
  const node2 = data.cluster_health[1];

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="space-y-4">
        <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4 font-mono text-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-cyan-400 font-bold">Cluster Status</h3>
            <span className="text-green-400 text-xs">● ONLINE</span>
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Active VMs:</span>
              <span className="text-white font-bold">{runningVMs}/{totalVMs}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Total Nodes:</span>
              <span className="text-white">2</span>
            </div>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4 font-mono text-sm">
          <h3 className="text-cyan-400 font-bold mb-3 text-xs">Node: {node1.name}</h3>
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-400">CPU</span>
                <span className="text-white">{node1.cpu.usage.toFixed(2)}%</span>
              </div>
              <div className="h-1.5 bg-slate-700 rounded overflow-hidden">
                <div className="h-full bg-cyan-500" style={{ width: `${node1.cpu.usage}%` }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-400">Memory</span>
                <span className="text-white">{node1.memory.usage_pct.toFixed(1)}%</span>
              </div>
              <div className="h-1.5 bg-slate-700 rounded overflow-hidden">
                <div className="h-full bg-blue-500" style={{ width: `${node1.memory.usage_pct}%` }}></div>
              </div>
            </div>
            <div className="flex justify-between text-xs pt-2 border-t border-cyan-500/20">
              <span className="text-slate-400">Uptime:</span>
              <span className="text-green-400">{node1.uptime}</span>
            </div>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4 font-mono text-sm">
          <h3 className="text-cyan-400 font-bold mb-3 text-xs">Node: {node2.name}</h3>
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-400">CPU</span>
                <span className="text-white">{node2.cpu.usage.toFixed(2)}%</span>
              </div>
              <div className="h-1.5 bg-slate-700 rounded overflow-hidden">
                <div className="h-full bg-cyan-500" style={{ width: `${node2.cpu.usage}%` }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-400">Memory</span>
                <span className="text-white">{node2.memory.usage_pct.toFixed(1)}%</span>
              </div>
              <div className="h-1.5 bg-slate-700 rounded overflow-hidden">
                <div className="h-full bg-blue-500" style={{ width: `${node2.memory.usage_pct}%` }}></div>
              </div>
            </div>
            <div className="flex justify-between text-xs pt-2 border-t border-cyan-500/20">
              <span className="text-slate-400">Uptime:</span>
              <span className="text-green-400">{node2.uptime}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4 font-mono text-sm">
        <h3 className="text-cyan-400 font-bold mb-3 text-xs">Virtual Machines</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {data.vms.map(vm => (
            <div key={vm.id} className="flex items-center justify-between py-2 px-3 bg-slate-800/50 rounded">
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${vm.status === 'running' ? 'bg-green-400' : 'bg-slate-600'}`}></span>
                <div>
                  <p className="text-white text-xs font-semibold">{vm.name}</p>
                  <p className="text-slate-500 text-xs">ID: {vm.id}</p>
                </div>
              </div>
              <div className="text-right">
                {vm.status === 'running' ? (
                  <>
                    <p className="text-xs text-slate-400">MEM: {vm.memory.toFixed(1)}%</p>
                    <p className="text-xs text-slate-400">CPU: {vm.cpu.toFixed(2)}%</p>
                  </>
                ) : (
                  <p className="text-xs text-slate-600">Stopped</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BypassITVisual() {
  const [step, setStep] = useState(0);
  const steps = [
    { phase: "init", text: "Executing AutoIT script..." },
    { phase: "detect", text: "Detecting EDR processes..." },
    { phase: "write", text: "Writing Windows Firewall rule..." },
    { phase: "block", text: "Blocking EDR connection..." },
    { phase: "complete", text: "Defense evasion complete ✓" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setStep(prev => (prev + 1) % steps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const currentStep = steps[step];
  const isBlocked = step >= 3;

  return (
    <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-6 font-mono text-sm">
      <div className="text-center mb-4">
        <p className="text-cyan-400 font-bold mb-2">EDR Bypass Simulation</p>
        <p className="text-slate-400 text-xs">AutoIT Script - Firewall Rule Creation</p>
      </div>

      <div className="bg-slate-950 border border-slate-700 rounded p-3 mb-4 min-h-32">
        <div className="space-y-1 text-xs">
          {steps.slice(0, step + 1).map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className={i === step ? 'text-cyan-400' : 'text-slate-500'}
            >
              <span className="text-green-400">&gt;</span> {s.text}
            </motion.div>
          ))}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-yellow-400 ml-4 text-xs"
            >
              netsh advfirewall firewall add rule name="Block_EDR" dir=out action=block remoteip=192.168.1.100
            </motion.div>
          )}
        </div>
      </div>

      <div className="relative h-32 flex items-center justify-between mb-4">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-2 border-cyan-500 rounded-lg flex items-center justify-center mb-2">
            <span className="text-2xl">💻</span>
          </div>
          <p className="text-xs text-slate-400">Endpoint</p>
        </div>

        <div className="flex-1 mx-4 relative">
          <motion.div
            className={`h-0.5 ${isBlocked ? 'bg-red-500' : 'bg-green-500'}`}
            animate={{ opacity: isBlocked ? [1, 0.3, 1] : 1 }}
            transition={{ duration: 0.5, repeat: isBlocked ? Infinity : 0 }}
          />
          {isBlocked && (
            <motion.div
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 text-white px-3 py-1 rounded text-xs font-bold"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
            >
              🚫 BLOCKED
            </motion.div>
          )}
        </div>

        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-2 border-cyan-500 rounded-lg flex items-center justify-center mb-2">
            <span className="text-2xl">🛡️</span>
          </div>
          <p className="text-xs text-slate-400">EDR Server</p>
          <p className="text-xs text-slate-600">192.168.1.100</p>
        </div>
      </div>

      <div className="pt-4 border-t border-cyan-500/20 text-xs space-y-1">
        <div className="flex justify-between">
          <span className="text-slate-400">Script:</span>
          <span className="text-cyan-400">bypass_edr.au3</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">Status:</span>
          <span className={isBlocked ? 'text-red-400' : 'text-green-400'}>
            {isBlocked ? 'EDR Blocked' : 'Monitoring...'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">Rule:</span>
          <span className="text-yellow-400">{isBlocked ? 'Active' : 'Pending'}</span>
        </div>
      </div>
    </div>
  );
}

function MalwareVisual() {
  const [injectionStep, setInjectionStep] = useState(0);
  const steps = [
    "Opening notepad.exe...",
    "Allocating virtual memory...",
    "Writing shellcode to memory...",
    "Creating remote thread...",
    "Injection complete ✓",
    "Establishing reverse TCP shell...",
    "Connection established!"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setInjectionStep(prev => (prev + 1) % steps.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const shellEstablished = injectionStep >= 6;

  return (
    <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-6 font-mono text-sm">
      <div className="text-center mb-4">
        <p className="text-cyan-400 font-bold mb-2">Process Injection Demo</p>
        <p className="text-slate-400 text-xs">Injecting reverse TCP shell into notepad.exe</p>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="space-y-3">
          <div className="bg-slate-800/50 border border-slate-700 rounded p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white text-xs">notepad.exe</span>
              <span className="text-slate-400 text-xs">PID: 1337</span>
            </div>
            <div className="h-2 bg-slate-700 rounded overflow-hidden">
              <motion.div
                className="h-full bg-cyan-500"
                initial={{ width: "0%" }}
                animate={{ width: injectionStep >= 2 ? "100%" : "0%" }}
                transition={{ duration: 0.5 }}
              />
            </div>
            {injectionStep >= 2 && injectionStep < 5 && (
              <p className="text-xs text-yellow-400 mt-2">Injecting shellcode...</p>
            )}
            {injectionStep >= 5 && (
              <p className="text-xs text-green-400 mt-2">✓ Shellcode injected</p>
            )}
          </div>

          <div className="space-y-1">
            {steps.slice(0, 5).map((step, i) => (
              <motion.div
                key={i}
                className={`text-xs px-2 py-1 rounded ${
                  i === injectionStep
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : i < injectionStep
                    ? 'text-green-400'
                    : 'text-slate-600'
                }`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: i <= injectionStep ? 1 : 0.3, x: 0 }}
              >
                {i < injectionStep && '✓ '}
                {i === injectionStep && '▸ '}
                {step}
              </motion.div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div className="bg-slate-800/50 border border-slate-700 rounded p-3 h-full">
            <p className="text-cyan-400 text-xs mb-3 font-bold">Reverse TCP Shell</p>
            
            {injectionStep >= 5 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-2"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 text-xs">Target:</span>
                  <span className="text-white text-xs">notepad.exe</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 text-xs">Attacker IP:</span>
                  <span className="text-cyan-400 text-xs">192.168.1.50</span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-400 text-xs">Port:</span>
                  <span className="text-cyan-400 text-xs">4444</span>
                </div>

                {shellEstablished && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="bg-green-500/20 border border-green-500/50 rounded p-2 text-center"
                  >
                    <p className="text-green-400 text-xs font-bold">✓ Shell Active</p>
                  </motion.div>
                )}

                {!shellEstablished && injectionStep === 5 && (
                  <div className="flex items-center justify-center">
                    <div className="animate-pulse text-yellow-400 text-xs">Connecting...</div>
                  </div>
                )}
              </motion.div>
            )}

            {injectionStep < 5 && (
              <div className="flex items-center justify-center h-full">
                <p className="text-slate-600 text-xs">Waiting for injection...</p>
              </div>
            )}
          </div>

          {injectionStep >= 5 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-1"
            >
              {steps.slice(5).map((step, i) => (
                <motion.div
                  key={i}
                  className={`text-xs px-2 py-1 rounded ${
                    i + 5 === injectionStep
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : i + 5 < injectionStep
                      ? 'text-green-400'
                      : 'text-slate-600'
                  }`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: i + 5 <= injectionStep ? 1 : 0.3, x: 0 }}
                >
                  {i + 5 < injectionStep && '✓ '}
                  {i + 5 === injectionStep && '▸ '}
                  {step}
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>

      <div className="pt-4 border-t border-cyan-500/20 text-xs grid grid-cols-2 gap-4">
        <div>
          <p className="text-slate-400">
            Technique: <span className="text-cyan-400">CreateRemoteThread</span>
          </p>
          <p className="text-slate-400">
            Framework: <span className="text-cyan-400">Metasploit</span>
          </p>
        </div>
        <div>
          <p className="text-slate-400">
            Payload: <span className="text-cyan-400">windows/meterpreter/reverse_tcp</span>
          </p>
          <p className="text-slate-400">
            Status: <span className={shellEstablished ? 'text-green-400' : 'text-yellow-400'}>
              {shellEstablished ? 'Active Shell' : 'In Progress'}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

function Portfolio() {
  const [activeSection, setActiveSection] = useState("hero");
  const [expandedProject, setExpandedProject] = useState(null);
  const titles = ["SOC Analyst", "Threat Hunter", "Security Researcher", "Cybersecurity Professional"];
  const [currentTitle, setCurrentTitle] = useState(0);
  const { displayedText: typedName } = useTypingEffect("Andrew Syverson", 120);
  const { displayedText: typedTitle } = useTypingEffect(titles[currentTitle], 80);

  useEffect(() => {
    const titleInterval = setInterval(() => {
      setCurrentTitle((prev) => (prev + 1) % titles.length);
    }, 4000);
    return () => clearInterval(titleInterval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["hero", "about", "experience", "projects", "education", "contact"];
      const scrollPos = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && scrollPos >= element.offsetTop && scrollPos < element.offsetTop + element.offsetHeight) {
          setActiveSection(section);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const experiences = [
    {
      role: "SOC Program Analyst 1",
      company: "360SOC",
      dates: "March 2024 – January 2025",
      bullets: [
        "Investigated and triaged security alerts using NetWitness SIEM and FortiSOAR with <5% escalation rate",
        "Improved detection capabilities by 15% through refined SIEM queries based on emerging threats",
        "Analyzed network telemetry from Palo Alto, Fortinet, Cisco ASA, SonicWall, and Meraki firewalls",
        "Monitored EDR/XDR solutions including SentinelOne, CrowdStrike, and NetWitness Endpoint",
        "Correlated security events with MITRE ATT&CK techniques for enhanced threat visibility"
      ]
    },
    {
      role: "IT Intern",
      company: "Troon",
      dates: "May – August 2024",
      bullets: [
        "Built AI agent leveraging Freshservice API data, saving company $50,000 in projected migration costs",
        "Developed natural language query system for IT troubleshooting based on historical ticket data",
        "Presented AI capabilities to executives, demonstrating technical value to non-technical stakeholders",
        "Configured and troubleshot Ubiquiti switches and Cisco Meraki firewalls",
        "Shadowed security team learning vulnerability management and email security processes"
      ]
    }
  ];

  const projects = [
    {
      id: "homelab",
      name: "Home SOC Lab",
      year: "2024-Present",
      description: "Built comprehensive security operations lab with Proxmox hypervisor, domain controller, Ubuntu web server, pfSense firewall, and Wazuh SIEM. Developed full-stack monitoring dashboards with live telemetry.",
      link: "https://dashboard.andrewsyverson.me",
      tags: ["Proxmox", "Wazuh", "SIEM", "Network Security"],
      component: <HomeLabDashboard />
    },
    {
      id: "bypassit",
      name: "BypassIT",
      year: "2024",
      description: "Contributed defense evasion script blocking outbound EDR connections for post-exploitation scenarios to open-source security research project.",
      link: "https://github.com/CroodSolutions/BypassIT",
      tags: ["Python", "EDR Evasion", "Red Team"],
      component: <BypassITVisual />
    },
    {
      id: "malware",
      name: "Malware Development & Analysis",
      year: "2024-Present",
      description: "Developed malware in C to understand common techniques including process injection, memory allocation, and shellcode execution using Metasploit.",
      tags: ["C", "Malware Analysis", "Process Injection"],
      component: <MalwareVisual />
    }
  ];

  return (
    <div className="bg-slate-950 min-h-screen relative">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 50 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full"
            initial={{
              x: Math.random() * 1920,
              y: Math.random() * 1080,
              opacity: Math.random() * 0.5
            }}
            animate={{
              y: [null, Math.random() * 1080],
              opacity: [null, 0, Math.random() * 0.5]
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
      </div>

      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-cyan-500/10"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <motion.a
              href="#hero"
              className="font-mono text-xl font-bold"
              whileHover={{ scale: 1.05 }}
            >
              <span className="text-cyan-400">&lt;</span>
              <span className="text-white">AS</span>
              <span className="text-cyan-400">/&gt;</span>
            </motion.a>
            
            <div className="flex gap-8">
              {["About", "Experience", "Projects", "Contact"].map((item) => (
                <motion.a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="relative font-mono text-sm text-slate-400 hover:text-cyan-400 transition-colors"
                  whileHover={{ y: -2 }}
                >
                  <span className="text-cyan-400 mr-1">./</span>
                  {item}
                  {activeSection === item.toLowerCase() && (
                    <motion.div
                      layoutId="navIndicator"
                      className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
                    />
                  )}
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </motion.nav>

      <section id="hero" className="relative min-h-[50vh] flex items-center justify-center px-6 pt-32 pb-20">
        <div className="max-w-5xl w-full">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <div className="mb-6">
              <h1 className="text-5xl md:text-6xl font-bold font-mono mb-4">
                <span className="text-cyan-400">&gt; </span>
                <span className="text-white">{typedName}</span>
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="text-cyan-400"
                >
                  _
                </motion.span>
              </h1>
              
              <div className="text-2xl md:text-3xl font-mono text-slate-400 h-12">
                <span className="text-cyan-400"># </span>
                {typedTitle}
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="text-cyan-400"
                >
                  |
                </motion.span>
              </div>
            </div>

            <motion.div 
              className="flex gap-4 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <motion.a
                href="#contact"
                className="px-6 py-3 bg-cyan-500 text-slate-950 font-mono font-bold rounded hover:bg-cyan-400 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get In Touch
              </motion.a>
              <motion.a
                href="#projects"
                className="px-6 py-3 border-2 border-cyan-500 text-cyan-400 font-mono font-bold rounded hover:bg-cyan-500/10 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View Work
              </motion.a>
            </motion.div>

            <motion.div
              className="flex gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              {[
                { name: "GitHub", url: "https://github.com/rayzax" },
                { name: "LinkedIn", url: "https://www.linkedin.com/in/andrew-syverson/" },
                { name: "Lab", url: "https://dashboard.andrewsyverson.me" }
              ].map((link) => (
                <motion.a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-cyan-400 font-mono text-sm transition-colors"
                  whileHover={{ y: -2 }}
                >
                  <span className="text-cyan-400 mr-1">→</span>
                  {link.name}
                </motion.a>
              ))}
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
      </section>

      <section id="about" className="py-20 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <motion.h2 
            className="text-3xl font-bold mb-8 font-mono"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-cyan-400">01.</span>
            <span className="text-white ml-4">About</span>
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid md:grid-cols-2 gap-8"
          >
            <div className="space-y-4 text-slate-300 leading-relaxed">
              <p>
                Proactive cybersecurity professional with hands-on experience in <span className="text-cyan-400 font-semibold">SOC operations</span>, <span className="text-cyan-400 font-semibold">threat hunting</span>, and <span className="text-cyan-400 font-semibold">incident response</span>.
              </p>
              <p>
                Proficient in security frameworks including <span className="text-cyan-400">MITRE ATT&CK</span>, <span className="text-cyan-400">NIST</span>, and <span className="text-cyan-400">PCI DSS</span> with extensive experience in SIEM solutions, EDR/XDR platforms, and SOAR automation.
              </p>
              <p>
                Passionate about AI applications in cybersecurity and continuous learning through hands-on lab projects.
              </p>
            </div>

            <div>
              <h3 className="text-cyan-400 font-mono mb-4">Technical Skills</h3>
              <div className="grid grid-cols-2 gap-3">
                {["Python", "C", "SIEM/SOAR", "EDR/XDR", "Network Security", "Threat Intel", "Incident Response", "Cloud Security"].map((skill) => (
                  <motion.div
                    key={skill}
                    className="bg-slate-900/50 border border-cyan-500/20 rounded px-4 py-2 text-sm text-slate-300 font-mono"
                    whileHover={{ borderColor: "rgba(6, 182, 212, 0.5)", x: 5 }}
                  >
                    <span className="text-cyan-400 mr-2">▹</span>
                    {skill}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="experience" className="py-20 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <motion.h2 
            className="text-3xl font-bold mb-12 font-mono"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-cyan-400">02.</span>
            <span className="text-white ml-4">Experience</span>
          </motion.h2>
          
          <div className="space-y-12">
            {experiences.map((exp, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2 }}
                className="relative pl-8 border-l-2 border-cyan-500/30 hover:border-cyan-500/60 transition-colors"
              >
                <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-cyan-500" />
                
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-white mb-1">{exp.role}</h3>
                  <p className="text-cyan-400 font-mono text-sm">{exp.company}</p>
                  <p className="text-slate-500 font-mono text-xs">{exp.dates}</p>
                </div>
                
                <ul className="space-y-2">
                  {exp.bullets.map((bullet, idx) => (
                    <motion.li
                      key={idx}
                      className="text-slate-300 flex items-start gap-3"
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.2 + 0.1 + idx * 0.05 }}
                    >
                      <span className="text-cyan-400 mt-1.5">▹</span>
                      <span>{bullet}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="projects" className="py-20 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <motion.h2 
            className="text-3xl font-bold mb-12 font-mono"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-cyan-400">03.</span>
            <span className="text-white ml-4">Projects</span>
          </motion.h2>
          
          <div className="space-y-8">
            {projects.map((project, i) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group relative bg-slate-900/30 border border-cyan-500/20 rounded-lg overflow-hidden hover:border-cyan-500/50 transition-all"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-white group-hover:text-cyan-400 transition-colors mb-1">{project.name}</h3>
                      <span className="text-xs text-slate-500 font-mono">{project.year}</span>
                    </div>
                    <motion.button
                      onClick={() => setExpandedProject(expandedProject === project.id ? null : project.id)}
                      className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-400 font-mono text-sm hover:bg-cyan-500/20 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {expandedProject === project.id ? 'Show Less' : 'See More'}
                    </motion.button>
                  </div>
                  
                  <p className="text-slate-300 mb-4 leading-relaxed">{project.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tags.map((tag) => (
                      <span key={tag} className="text-xs font-mono text-cyan-400 bg-cyan-500/10 px-3 py-1 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  {project.link && (
                    <motion.a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-cyan-400 font-mono text-sm hover:gap-3 transition-all"
                    >
                      View Project
                      <span>→</span>
                    </motion.a>
                  )}
                </div>

                <AnimatePresence>
                  {expandedProject === project.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-cyan-500/20 overflow-hidden"
                    >
                      <div className="p-6">
                        {project.component}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="education" className="py-20 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <motion.h2 
            className="text-3xl font-bold mb-12 font-mono"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-cyan-400">04.</span>
            <span className="text-white ml-4">Education & Certifications</span>
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Grand Canyon University", subtitle: "B.S. in Cybersecurity", detail: "GPA: 3.58 | Dec 2025" },
              { title: "CompTIA Security+", subtitle: "Certification 701", detail: "June 2024 – June 2027" },
              { title: "TISU ACTRA", subtitle: "Threat Intelligence", detail: "December 2024" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-slate-900/30 border border-cyan-500/20 rounded-lg p-6 hover:border-cyan-500/50 transition-all"
              >
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-cyan-400 text-sm mb-2">{item.subtitle}</p>
                <p className="text-slate-400 text-xs font-mono">{item.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-32 px-6 relative">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 font-mono">
              <span className="text-cyan-400">&gt; </span>
              <span className="text-white">Let's Connect</span>
            </h2>
            <p className="text-slate-400 mb-8 text-lg">
              Interested in collaborating or learning more about my work?
            </p>
            
            <motion.a
              href="mailto:Ajsyverson03@gmail.com"
              className="inline-block text-2xl font-mono text-cyan-400 hover:text-cyan-300 mb-8 transition-colors"
              whileHover={{ scale: 1.05 }}
            >
              Ajsyverson03@gmail.com
            </motion.a>
            
            <div className="flex gap-8 justify-center">
              {[
                { name: "LinkedIn", url: "https://www.linkedin.com/in/andrew-syverson/" },
                { name: "GitHub", url: "https://github.com/rayzax" },
                { name: "Lab Environment", url: "https://dashboard.andrewsyverson.me" }
              ].map((link) => (
                <motion.a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-cyan-400 font-mono transition-colors"
                  whileHover={{ y: -3 }}
                >
                  {link.name}
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="py-8 text-center border-t border-cyan-500/10">
        <p className="text-slate-500 font-mono text-sm">
          © 2025 Andrew Syverson | Built with React & Tailwind
        </p>
      </footer>
    </div>
  );
}

export default function App() {
  const [stage, setStage] = useState("login");

  return (
    <>
      {stage === "login" && <FakeLogin onAuth={() => setStage("boot")} />}
      {stage === "boot" && <TerminalIntro onFinish={() => setStage("main")} />}
      {stage === "main" && <Portfolio />}
    </>
  );
}
